import React, { useEffect, useState, useRef } from 'react';
import { X, BookOpen, CheckCircle, ExternalLink, AlertCircle, Sparkles, FileText } from 'lucide-react';
import { processOptimizationAPI, WorkflowBlueprint } from '../../services/processOptimizationAPI';

interface WorkflowViewerProps {
  opportunityId: number;
  opportunityTitle: string;
  onClose: () => void;
}

const WorkflowViewer: React.FC<WorkflowViewerProps> = ({ opportunityId, opportunityTitle, onClose }) => {
  const [workflows, setWorkflows] = useState<WorkflowBlueprint[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowBlueprint | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useAI, setUseAI] = useState<boolean | null>(null); // null = not selected yet
  const [modeSelected, setModeSelected] = useState(false);
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const loadingRef = useRef(false);
  const loadedOpportunityRef = useRef<number | null>(null);
  const loadedWithAIRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Don't load until mode is selected
    if (!modeSelected || useAI === null) {
      return;
    }

    // Prevent duplicate calls for the same opportunity with same AI setting (unless force regenerate)
    if (loadingRef.current ||
        (!forceRegenerate && loadedOpportunityRef.current === opportunityId && loadedWithAIRef.current === useAI)) {
      return;
    }

    loadWorkflows();
  }, [opportunityId, useAI, forceRegenerate, modeSelected]);

  const loadWorkflows = async () => {
    // Double-check to prevent race conditions
    if (loadingRef.current || useAI === null) {
      console.log('⚠️ Workflow loading already in progress or mode not selected, skipping...');
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      console.log(`📥 Loading workflows for opportunity ${opportunityId} (AI: ${useAI}, forceRegenerate: ${forceRegenerate})...`);
      const data = await processOptimizationAPI.getWorkflows(opportunityId, true, useAI, forceRegenerate);

      console.log(`✅ Loaded ${data.length} workflow(s)`);
      setWorkflows(data);
      loadedOpportunityRef.current = opportunityId;
      loadedWithAIRef.current = useAI;
      setForceRegenerate(false); // Reset force regenerate after successful load

      if (data.length > 0) {
        setSelectedWorkflow(data[0]);
      }
    } catch (err: any) {
      console.error('❌ Failed to load workflows:', err);
      setError(err.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const handleSelectAIMode = () => {
    setUseAI(true);
    setModeSelected(true);
  };

  const handleSelectTemplateMode = () => {
    setUseAI(false);
    setModeSelected(true);
  };

  const handleRegenerateWithAI = () => {
    if (useAI && workflows.length > 0) {
      // Already on AI mode with workflows, no need to regenerate
      return;
    }
    // Reset refs and force regeneration
    loadedOpportunityRef.current = null;
    loadedWithAIRef.current = null;
    setWorkflows([]);
    setSelectedWorkflow(null);
    setForceRegenerate(true);
    setUseAI(true);
  };

  const handleRegenerateWithoutAI = () => {
    if (!useAI && workflows.length > 0) {
      // Already on template mode with workflows, no need to regenerate
      return;
    }
    // Reset refs and force regeneration
    loadedOpportunityRef.current = null;
    loadedWithAIRef.current = null;
    setWorkflows([]);
    setSelectedWorkflow(null);
    setForceRegenerate(true);
    setUseAI(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Implementation Guide</h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate" title={opportunityTitle}>{opportunityTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </button>
          </div>

          {/* AI Toggle - Only show when not loading */}
          {!loading && workflows.length > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white/50 rounded-lg p-3 border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <span className="text-xs sm:text-sm font-medium text-gray-700">Generation Mode:</span>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={handleRegenerateWithAI}
                    disabled={loading}
                    className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      useAI
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">AI-Generated</span>
                    <span className="sm:hidden">AI</span>
                  </button>
                  <button
                    onClick={handleRegenerateWithoutAI}
                    disabled={loading}
                    className={`flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      !useAI
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Template</span>
                    <span className="sm:hidden">Tmpl</span>
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {useAI ? 'Detailed AI instructions (1-3 min)' : 'Quick template guide (instant)'}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Mode Selection Screen - Show first before any generation */}
          {!modeSelected && !loading && (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="text-center max-w-lg mx-auto">
                <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Choose Generation Mode</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Select how you want to generate the implementation guide for this opportunity.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* AI Option */}
                  <button
                    onClick={handleSelectAIMode}
                    className="group p-4 sm:p-6 border-2 border-purple-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                        <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm sm:text-base">AI-Generated</h4>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3">
                      Detailed, context-aware instructions tailored specifically for your use case using AI.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-purple-600 font-medium">
                      <span>⏱️ Takes 1-3 minutes</span>
                    </div>
                  </button>

                  {/* Template Option */}
                  <button
                    onClick={handleSelectTemplateMode}
                    className="group p-4 sm:p-6 border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm sm:text-base">Template-Based</h4>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-3">
                      Quick guide using pre-built templates with your compliance requirements.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
                      <span>⚡ Instant generation</span>
                    </div>
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  You can switch between modes after generation is complete.
                </p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center max-w-md mx-auto">
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4 ${useAI ? 'border-purple-600' : 'border-blue-600'}`}></div>
                <p className="text-gray-800 font-semibold mb-2">
                  {useAI ? 'Generating AI-Powered Implementation Guide...' : 'Generating Template-Based Guide...'}
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  {useAI
                    ? 'AI is creating detailed step-by-step instructions tailored for your use case. This may take 1-3 minutes depending on complexity.'
                    : 'Creating a quick template-based guide using your compliance requirements. This should only take a few seconds.'
                  }
                </p>
                <p className={`text-xs font-medium ${useAI ? 'text-purple-600' : 'text-blue-600'}`}>
                  {useAI
                    ? '⏳ Please wait... The backend is processing your request with AI'
                    : '⚡ Almost ready... Generating template-based instructions'
                  }
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Error</h3>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {modeSelected && !loading && !error && workflows.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No workflow blueprints available</p>
            </div>
          )}

          {!loading && !error && workflows.length > 0 && selectedWorkflow && (
            <div className="space-y-6">
              {/* Workflow Tabs */}
              {workflows.length > 1 && (
                <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
                  {workflows.map((workflow) => (
                    <button
                      key={workflow.id}
                      onClick={() => setSelectedWorkflow(workflow)}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        selectedWorkflow.id === workflow.id
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {workflow.workflow_type}
                    </button>
                  ))}
                </div>
              )}

              {/* Workflow Details */}
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{selectedWorkflow.workflow_name}</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">{selectedWorkflow.description}</p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs sm:text-sm text-blue-900">
                      <strong>Workflow Type:</strong> {selectedWorkflow.workflow_type}
                    </div>
                  </div>
                </div>
              </div>

              {/* Prerequisites */}
              {selectedWorkflow.prerequisites && selectedWorkflow.prerequisites.length > 0 && (
                <div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Prerequisites</h4>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <ul className="space-y-2">
                      {selectedWorkflow.prerequisites.map((prereq, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span className="break-words">{prereq}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Step-by-Step Instructions */}
              {selectedWorkflow.step_by_step_instructions && selectedWorkflow.step_by_step_instructions.length > 0 && (
                <div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Step-by-Step Instructions</h4>
                  <div className="space-y-3 sm:space-y-4">
                    {selectedWorkflow.step_by_step_instructions.map((step: any, idx: number) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-lg flex items-center justify-center font-bold text-sm sm:text-base">
                            {step.stepNumber}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">{step.title}</h5>

                            {/* Render sub-steps if available */}
                            {step.subSteps && step.subSteps.length > 0 ? (
                              <ol className="list-none space-y-2 mb-2">
                                {step.subSteps.map((subStep: string, subIdx: number) => (
                                  <li key={subIdx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                                    <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                                      {String.fromCharCode(97 + subIdx)}
                                    </span>
                                    <span className="flex-1 pt-0.5 break-words">{subStep}</span>
                                  </li>
                                ))}
                              </ol>
                            ) : (
                              /* Fallback to old instruction format for backward compatibility */
                              step.instruction && (
                                <div className="text-xs sm:text-sm text-gray-700 whitespace-pre-line mb-2">{step.instruction}</div>
                              )
                            )}

                            {step.expectedResult && (
                              <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs sm:text-sm text-green-800 mt-2">
                                <strong>Expected Result:</strong> {step.expectedResult}
                              </div>
                            )}
                            {step.complianceNote && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-xs sm:text-sm text-purple-800 mt-2">
                                <strong>🔒 Compliance:</strong> {step.complianceNote}
                              </div>
                            )}
                            {step.tips && step.tips.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {step.tips.map((tip: string, tipIdx: number) => (
                                  <div key={tipIdx} className="text-xs text-blue-700 flex items-start gap-1">
                                    <span>💡</span>
                                    <span className="break-words">{tip}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Troubleshooting */}
              {selectedWorkflow.troubleshooting_tips && selectedWorkflow.troubleshooting_tips.length > 0 && (
                <div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Troubleshooting</h4>
                  <div className="space-y-3">
                    {selectedWorkflow.troubleshooting_tips.map((tip: any, idx: number) => (
                      <div key={idx} className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                        <h5 className="font-bold text-orange-900 mb-2 text-sm sm:text-base">Problem: {tip.problem}</h5>
                        <p className="text-xs sm:text-sm text-orange-800"><strong>Solution:</strong> {tip.solution}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* External Resources */}
              {selectedWorkflow.external_resources && selectedWorkflow.external_resources.length > 0 && (
                <div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3">Additional Resources</h4>
                  <div className="space-y-2">
                    {selectedWorkflow.external_resources.map((resource: any, idx: number) => (
                      <a
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 sm:p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{resource.title}</div>
                          <div className="text-xs text-gray-500">{resource.type}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowViewer;
